import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import ReactGA from 'react-ga4';
import PageTracking from './components/PageTracking';
import { isMobilePhoneBrowser } from './utils/deviceDetection';

import JusticeMission from './components/JusticeMission';
import LandingPage from './landing/LandingPage';
import PrivacyPage from './landing/PrivacyPage';
import TermsPage from './landing/TermsPage';
import DemosPage from './landing/DemosPage';
import UploadDocument from './components/UploadDocument';
import UploadResponse from './components/UploadResponse';
import UploadStatement from './components/UploadStatement';
import FIR from './components/FIR';
import Rights from './components/Rights';
import RegistrationForm from './components/RegistrationForm';
import Login from './components/Login'
import ResetPassword from './components/ResetPassword'
import AboutUs from './components/AboutUs';
import MyCases from './components/MyCases';

import Blogs from './components/Blogs';
import CriminalCaseGuide from './components/blogs/CriminalCaseGuide';
import FailedMarriageNotCrime from './components/blogs/FailedMarriageIsNotCrime';
import ProperPoliceInvestigation from './components/blogs/ProperPoliceInvestigation';
import PIApplications from './components/blogs/PIApplications';
import FIRComplaint from './components/FIRComplaint';
import DraftFIR from './components/DraftFIR';
import InvestigateFIR from './components/InvestigateFIR';
import DownloadApp from './components/DownloadApp';
import InvestigationDashboardPage from './components/InvestigationDashboard';
import UploadCaseDiary from './components/UploadCaseDiary';
import ChargeSheetView from './components/GeneratedCS';
import UploadChargeSheet from './components/UploadChargeSheet';
import ChargeSheetsList from './components/ChargeSheetsList';
import VerifiedChargeSheet from './components/VerifiedCS';
import UserProfile from './components/UserProfile';
import AdminPage from './components/AdminPage';

import MobileLayout from './mobile/components/MobileLayout';
import MobileLogin from './mobile/screens/Login';
import MobileRegister from './mobile/screens/Register';
import MobileResetPassword from './mobile/screens/ResetPassword';
import MobileAboutUs from './mobile/screens/AboutUs';
import MobileTextPage from './mobile/screens/TextPage';
import MobileLanguage from './mobile/screens/Language';
import MobileState from './mobile/screens/State';
import MobileMyCases from './mobile/screens/MyCases';
import MobileCaseDetails from './mobile/screens/CaseDetails';
import MobileShareCase from './mobile/screens/ShareCase';
import MobileInvestigationDashboard from './mobile/screens/InvestigationDashboard';
import CaseLayout from './mobile/components/CaseLayout';
import MobileTimeline from './mobile/screens/TimelineScreen';
import MobileTasks from './mobile/screens/Tasks';
import MobileNewTask from './mobile/screens/NewTask';
import MobileEvidenceList from './mobile/screens/EvidenceList';
import MobileNewEvidence from './mobile/screens/NewEvidence';
import MobileWitnessList from './mobile/screens/WitnessList';
import MobileNewWitness from './mobile/screens/NewWitness';
import MobileQuestions from './mobile/screens/Questions';
import MobileRecordAnswer from './mobile/screens/RecordAnswer';
import MobileAccusedList from './mobile/screens/AccusedList';
import MobileNewAccused from './mobile/screens/NewAccused';
import MobileStatements from './mobile/screens/Statements';
import MobileStatement from './mobile/screens/Statement';
import MobileStatementDashboard from './mobile/screens/StatementDashboard';
import MobileDiscrepancy from './mobile/screens/Discrepancy';
import MobileNewCase from './mobile/screens/NewCase';
import MobileNewStatement from './mobile/screens/NewStatement';
import MobileNewCaseDiary from './mobile/screens/NewCaseDiary';
import MobileNewChargeSheet from './mobile/screens/NewChargeSheet';
import MobileChargeSheetsScreen from './mobile/screens/ChargeSheetsScreen';
import MobileGeneratedCS from './mobile/screens/GeneratedCS';
import MobileVerifiedCSDetails from './mobile/screens/VerfiedCSDetailsScreen';
import MobileUpdateProfile from './mobile/screens/UpdateProfile';
import MobileAdminPage from './mobile/screens/AdminPage';

ReactGA.initialize('G-X919BG47PW');

// The co-branded i4c site keeps its existing home page; every other host gets the new one.
const isI4CHost = window.location.hostname.toLowerCase() === 'i4c.legalapp.in';
console.log(`[App] home page: ${isI4CHost ? 'JusticeMission (i4c host)' : 'LandingPage'}`);

function App() {
  useEffect(() => {
    document.title = "LegalApp"; // Update tab title

    // Desktop has no persistent-login feature, so every load starts fresh.
    // Mobile-web intentionally keeps a valid JWT across loads (see JusticeMission's
    // hasValidSession check) to mirror the app's "stay logged in" feel, so skip the wipe there.
    if (!isMobilePhoneBrowser()) {
      localStorage.removeItem('jwtToken');
      localStorage.removeItem('email');
      localStorage.removeItem('role');
      localStorage.removeItem('firstName');
    }
  }, []);

  // App.js

  function getApiUrl() {
    /*
    const hostname = window.location.hostname.toLowerCase();

    if (hostname === "legalapp.in" || hostname === "www.legalapp.in") {
      return "https://legalapp-638589756238.asia-south1.run.app";
    } else if (hostname === "localhost" || hostname === "127.0.0.1") {
      return "http://localhost:8080";
    } else {
      //custom domains
      return "https://legalapp-ai-638589756238.asia-south1.run.app";
    }
    */
  }

  // Local and Cloud Run API:
  //const API_URL = 'http://localhost:8080';
  //const API_URL = 'https://legalapp-638589756238.asia-south1.run.app';
  const API_URL = 'https://legalapp-ai-638589756238.asia-south1.run.app';

  return (
    <Router>
      <PageTracking />
      <Routes>
        <Route path="/" element={isI4CHost ? <JusticeMission /> : <LandingPage />} />
        <Route path="/about" element={<AboutUs />} />
        <Route path="/blogs" element={<Blogs />} />
        <Route path="/register" element={<RegistrationForm API_URL={API_URL} />} />
        <Route path="/login" element={<Login API_URL={API_URL} />} />
        <Route path="/profile" element={<UserProfile API_URL={API_URL} />} />
        <Route path="/admin" element={<AdminPage API_URL={API_URL} />} />
        <Route path="/fir/dashboard" element={<InvestigationDashboardPage API_URL={API_URL} />} />
        <Route path="/app" element={<DownloadApp API_URL={API_URL} />} />
        <Route path="/reset" element={<ResetPassword API_URL={API_URL} />} />
        <Route path="/upload" element={<UploadDocument API_URL={API_URL} />} />
        <Route path="/draft" element={<DraftFIR API_URL={API_URL} />} />
        <Route path="/response" element={<UploadResponse API_URL={API_URL} />} />
        <Route path="/fir/statement" element={<UploadStatement API_URL={API_URL} />} />
        <Route path="/fir/casediary/upload" element={<UploadCaseDiary API_URL={API_URL} />} />
        <Route path="/fir/chargesheet/upload" element={<UploadChargeSheet API_URL={API_URL} />} />
        <Route path="/fir/chargesheet" element={<ChargeSheetsList API_URL={API_URL} />} />
        <Route path="/fir/chargesheet/generated" element={<ChargeSheetView API_URL={API_URL} />} />
        <Route path="/fir/chargesheet/verified" element={<VerifiedChargeSheet API_URL={API_URL} />} />
        <Route path="/fir" element={<FIR API_URL={API_URL} />} />
        <Route path="/investigate/fir" element={<InvestigateFIR API_URL={API_URL} />} />
        <Route path="/fir/timeline" element={<InvestigateFIR API_URL={API_URL} section="timeline" />} />
        <Route path="/fir/tasks" element={<InvestigateFIR API_URL={API_URL} section="tasks" />} />
        <Route path="/fir/evidences" element={<InvestigateFIR API_URL={API_URL} section="evidences" />} />
        <Route path="/fir/witnesses" element={<InvestigateFIR API_URL={API_URL} section="witnesses" />} />
        <Route path="/fir/accused" element={<InvestigateFIR API_URL={API_URL} section="accused" />} />
        <Route path="/fir/statements" element={<InvestigateFIR API_URL={API_URL} section="statements" />} />
        <Route path="/fir/discrepancies" element={<InvestigateFIR API_URL={API_URL} section="discrepancies" />} />
        <Route path="/fir/complaint" element={<FIRComplaint API_URL={API_URL} />} />
        <Route path="/mycases" element={<MyCases API_URL={API_URL} />} />
        <Route path="/rights" element={<Rights />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/demos" element={<DemosPage />} />
        <Route path="/blogs/criminal-case-guide" element={<CriminalCaseGuide />} />
        <Route path="/blogs/failed-marriage-not-crime" element={<FailedMarriageNotCrime />} />
        <Route path="/blogs/proper-police-investigation" element={<ProperPoliceInvestigation />} />
        <Route path="/blogs/ai-applications" element={<PIApplications />} />

        {/* Mobile-mirrored screens, served to phone-browser users. Screens added phase by phase. */}
        {/* Terms/Privacy are legal content shared verbatim with desktop, rendered without mobile chrome. */}
        <Route path="/m/terms" element={<TermsPage />} />
        <Route path="/m/privacy" element={<PrivacyPage />} />

        <Route path="/m" element={<MobileLayout API_URL={API_URL} />}>
          <Route path="login" element={<MobileLogin API_URL={API_URL} />} />
          <Route path="register" element={<MobileRegister API_URL={API_URL} />} />
          <Route path="reset-password" element={<MobileResetPassword API_URL={API_URL} />} />
          <Route path="about" element={<MobileAboutUs API_URL={API_URL} />} />
          <Route path="text/:key" element={<MobileTextPage API_URL={API_URL} />} />
          <Route path="language" element={<MobileLanguage API_URL={API_URL} />} />
          <Route path="state" element={<MobileState API_URL={API_URL} />} />
          <Route path="cases" element={<MobileMyCases API_URL={API_URL} />} />
          <Route path="cases/details" element={<MobileCaseDetails API_URL={API_URL} />} />
          <Route path="cases/share" element={<MobileShareCase API_URL={API_URL} />} />
          <Route path="cases/new" element={<MobileNewCase API_URL={API_URL} />} />
          <Route path="profile" element={<MobileUpdateProfile API_URL={API_URL} />} />
          <Route path="admin" element={<MobileAdminPage API_URL={API_URL} />} />

          <Route path="fir" element={<CaseLayout />}>
            <Route path="dashboard" element={<MobileInvestigationDashboard API_URL={API_URL} />} />
            <Route path="timeline" element={<MobileTimeline API_URL={API_URL} />} />
            <Route path="tasks" element={<MobileTasks API_URL={API_URL} />} />
            <Route path="tasks/new" element={<MobileNewTask API_URL={API_URL} />} />
            <Route path="evidences" element={<MobileEvidenceList API_URL={API_URL} />} />
            <Route path="evidences/new" element={<MobileNewEvidence API_URL={API_URL} />} />
            <Route path="witnesses" element={<MobileWitnessList API_URL={API_URL} />} />
            <Route path="witnesses/new" element={<MobileNewWitness API_URL={API_URL} />} />
            <Route path="witnesses/questions" element={<MobileQuestions API_URL={API_URL} />} />
            <Route path="witnesses/questions/answer" element={<MobileRecordAnswer API_URL={API_URL} />} />
            <Route path="accused" element={<MobileAccusedList API_URL={API_URL} />} />
            <Route path="accused/new" element={<MobileNewAccused API_URL={API_URL} />} />
            <Route path="statements" element={<MobileStatements API_URL={API_URL} />} />
            <Route path="statements/view" element={<MobileStatement API_URL={API_URL} />} />
            <Route path="statements/dashboard" element={<MobileStatementDashboard API_URL={API_URL} />} />
            <Route path="statements/new" element={<MobileNewStatement API_URL={API_URL} />} />
            <Route path="discrepancies" element={<MobileDiscrepancy API_URL={API_URL} />} />
            <Route path="casediary/new" element={<MobileNewCaseDiary API_URL={API_URL} />} />
            <Route path="chargesheet" element={<MobileChargeSheetsScreen API_URL={API_URL} />} />
            <Route path="chargesheet/new" element={<MobileNewChargeSheet API_URL={API_URL} />} />
            <Route path="chargesheet/generated" element={<MobileGeneratedCS API_URL={API_URL} />} />
            <Route path="chargesheet/verified" element={<MobileVerifiedCSDetails API_URL={API_URL} />} />
          </Route>
        </Route>
      </Routes>
    </Router>
  );
}

export default App;
